// src/pages/PublicWebsite.js
// Public-facing website page rendered from a practitioner's slug.
// Route: /site/:slug (no Layout, no auth required)
//
// Flow:
//   1. Read :slug param
//   2. Fetch slugs/{slug} → practitionerId
//   3. Fetch practitioners/{practitionerId} → website_published + availability
//   4. Render WebsiteRenderer with fetched data

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db, auth } from '../config/firebase-config';
import { doc, getDoc, updateDoc, increment, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import WebsiteRenderer from '../components/website-preview/WebsiteRenderer';

const DEFAULT_OG_IMAGE = 'https://divineframeworks.com/og-default.png';
const SITE_BASE_URL = 'https://divineframeworks.com';

// Strip HTML tags and return plain text (for meta descriptions)
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

// Build the meta description from About text or a fallback
function buildMetaDescription(published, name) {
  const raw = published?.content?.about || published?.profile?.motivation || '';
  const plain = stripHtml(raw);
  if (plain.length > 10) {
    return plain.length > 155 ? plain.slice(0, 152) + '…' : plain;
  }
  return `${name} is a spiritual practitioner on Divine Frameworks. Book a session today.`;
}

// Get the primary modality label from the published profile
function getPrimaryModality(published) {
  const modalities = published?.profile?.modalities;
  if (!modalities) return null;
  const first = modalities.selected?.[0] || modalities.custom?.[0] || null;
  return first || null;
}

// Only use hero image if it's a real http(s) URL — matches HeroSettings.js field names
function getHeroImageUrl(published) {
  const hero = published?.hero || {};
  const candidates = [
    hero.imageUrl,        // Unsplash image (type === 'image')
    hero.customImageUrl,  // custom upload (type === 'custom-image')
    hero.backgroundImage, // legacy field
  ];
  for (const img of candidates) {
    if (img && typeof img === 'string' && img.startsWith('http')) return img;
  }
  return DEFAULT_OG_IMAGE;
}

// Build JSON-LD structured data
function buildJsonLd(name, modality, description, imageUrl, slug, services, reviewStats) {
  const fullTitle = modality ? `${name} — ${modality}` : name;
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: fullTitle,
    description,
    url: `${SITE_BASE_URL}/site/${slug}`,
    image: imageUrl,
    provider: { '@type': 'Person', name },
  };
  if (services && services.length > 0) {
    ld.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: 'Services',
      itemListElement: services.slice(0, 10).map(s => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: s.name || '',
          description: s.tagline || s.description || '',
          ...(s.price != null && {
            offers: {
              '@type': 'Offer',
              price: String(s.price),
              priceCurrency: 'USD',
            },
          }),
        },
      })),
    };
  }
  if (reviewStats && reviewStats.totalReviews > 0) {
    ld.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: reviewStats.averageRating,
      reviewCount: reviewStats.totalReviews,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return JSON.stringify(ld);
}

/** Apply title case to a name string */
function toTitleCase(str) {
  if (!str) return str;
  return str.replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
}

// Helper to derive a display name from the published profile data + full Firestore doc
function getNameFromPublished(published, data = {}) {
  const raw =
    published?.profile?.displayName ||
    published?.hero?.headline ||
    data?.profile?.displayName ||
    data?.profile?.fullName ||
    data?.onboarding?.data?.fullName ||
    'Practitioner';
  return toTitleCase(raw);
}

// Loading screen
const LoadingScreen = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #E5E7EB',
        borderTopColor: '#7c3aed',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 16px',
      }} />
      <p style={{ fontSize: '14px', color: '#6B7280', fontFamily: 'system-ui, sans-serif' }}>
        Loading...
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

// 404 / Not Found screen
const NotFoundScreen = ({ slug }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    fontFamily: 'system-ui, sans-serif',
  }}>
    <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px 24px' }}>
      <div style={{
        width: '64px',
        height: '64px',
        backgroundColor: '#F3F4F6',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
        fontSize: '28px',
      }}>
        🔮
      </div>
      <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
        Page not found
      </h1>
      <p style={{ fontSize: '15px', color: '#6B7280', marginBottom: '24px', lineHeight: '1.6' }}>
        {slug
          ? `We couldn't find a practitioner at "${slug}".`
          : 'This page does not exist.'}
        {' '}The link may be incorrect or the practitioner may have changed their address.
      </p>
      <a
        href="/"
        style={{
          display: 'inline-block',
          padding: '10px 24px',
          backgroundColor: '#7c3aed',
          color: 'white',
          borderRadius: '8px',
          textDecoration: 'none',
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        Go to Divine Frameworks
      </a>
    </div>
  </div>
);

// Coming-soon screen (site exists but nothing published yet)
const ComingSoonScreen = ({ name }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)',
    fontFamily: 'system-ui, sans-serif',
  }}>
    <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px 24px', color: 'white' }}>
      <div style={{ fontSize: '48px', marginBottom: '24px' }}>✨</div>
      <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
        {name ? `${name}'s` : 'This'} website is coming soon
      </h1>
      <p style={{ fontSize: '15px', opacity: 0.85, lineHeight: '1.6' }}>
        This practitioner is setting up their Divine Frameworks website. Check back soon!
      </p>
    </div>
  </div>
);

// Error screen
const ErrorScreen = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    fontFamily: 'system-ui, sans-serif',
  }}>
    <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px 24px' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
        Something went wrong
      </h1>
      <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px' }}>
        We couldn't load this page. Please try refreshing.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 24px',
          backgroundColor: '#7c3aed',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Refresh
      </button>
    </div>
  </div>
);

const PublicWebsite = ({ slug: slugProp }) => {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  const [status, setStatus] = useState('loading'); // loading | not-found | coming-soon | ready | error
  const [websiteData, setWebsiteData] = useState(null);
  const [practitionerId, setPractitionerId] = useState(null);
  const [practitionerName, setPractitionerName] = useState('');
  const [verification, setVerification] = useState(null);
  const [availability, setAvailability] = useState({});
  const [publishedReviews, setPublishedReviews] = useState([]);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [policies, setPolicies] = useState(null);
  const [practitionerProfile, setPractitionerProfile] = useState(null);
  const hasCountedView = useRef(false);

  useEffect(() => {
    if (!slug) {
      setStatus('not-found');
      return;
    }
    loadWebsite(slug);
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWebsite = async (slugValue) => {
    try {
      // Step 1: Look up slug → practitionerId
      const slugDoc = await getDoc(doc(db, 'slugs', slugValue));
      if (!slugDoc.exists()) { setStatus('not-found'); return; }

      const pid = slugDoc.data().practitionerId;
      if (!pid) { setStatus('not-found'); return; }

      // Step 2: Read practitioner doc (has website_published + availability)
      const practDoc = await getDoc(doc(db, 'practitioners', pid));
      if (!practDoc.exists()) { setStatus('not-found'); return; }

      const data = practDoc.data();
      console.log(
        'PublicWebsite: loaded practitioner data,' +
        ' website_published exists:', !!data.website_published,
        '| size:', JSON.stringify(data.website_published || {}).length, 'bytes'
      );
      const published = data.website_published || null;
      const avail = data.availability || {};
      const name = getNameFromPublished(published, data);

      setPractitionerId(pid);
      setPractitionerName(name);
      setVerification(data.verification || null);
      setAvailability(avail);
      setReviewStats(data.reviewStats || null);
      setPolicies(data.policies || null);
      setPractitionerProfile(data.profile || null);

      if (!published) { setStatus('coming-soon'); return; }

      setWebsiteData(published);
      setStatus('ready');

      // Fetch DF published reviews (fire-and-forget, non-blocking)
      getDocs(query(
        collection(db, 'reviews'),
        where('practitionerId', '==', pid),
        where('status', '==', 'published'),
        orderBy('submittedAt', 'desc')
      )).then(snap => {
        setPublishedReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }).catch(() => {});

      // Fetch Google reviews (fire-and-forget, non-blocking)
      getDocs(query(
        collection(db, 'practitioners', pid, 'googleReviews'),
        orderBy('time', 'desc')
      )).then(snap => {
        setGoogleReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }).catch(() => {});

      // Fire-and-forget page view counter — skip if practitioner is viewing their own site
      if (!hasCountedView.current) {
        hasCountedView.current = true;
        if (!auth?.currentUser || auth.currentUser.uid !== pid) {
          const now = new Date();
          const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          updateDoc(doc(db, 'practitioners', pid), {
            [`siteViews.${yearMonth}`]: increment(1)
          }).catch(() => {});
        }
      }
    } catch (err) {
      console.error('PublicWebsite load error:', err);
      setStatus('error');
    }
  };

  if (status === 'loading') return <LoadingScreen />;
  if (status === 'not-found') return <NotFoundScreen slug={slug} />;
  if (status === 'coming-soon') return <ComingSoonScreen name={practitionerName} />;
  if (status === 'error') return <ErrorScreen />;

  // status === 'ready'
  const { design, hero, content, contactFooter, marketing, profile,
    sectionOrder, sectionVisibility } = websiteData;

  const metaName = practitionerName;
  const metaModality = getPrimaryModality(websiteData);
  const metaDescription = buildMetaDescription(websiteData, metaName);
  const metaImage = getHeroImageUrl(websiteData);
  const metaTitle = metaModality ? `${metaName} — ${metaModality}` : metaName;
  const metaUrl = `${SITE_BASE_URL}/site/${slug}`;
  const jsonLd = buildJsonLd(metaName, metaModality, metaDescription, metaImage, slug, content?.services, reviewStats);

  return (
    <>
      <Helmet>
        <title>{metaTitle} | Divine Frameworks</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Divine Frameworks" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>
      <WebsiteRenderer
      design={design}
      hero={hero}
      content={content}
      contactFooter={contactFooter}
      marketing={marketing}
      profile={profile}
      sectionOrder={sectionOrder}
      sectionVisibility={sectionVisibility}
      practitionerId={practitionerId}
      practitionerName={practitionerName}
      verification={verification}
      availability={availability}
      publishedReviews={publishedReviews}
      googleReviews={googleReviews}
      embedded={false}
      isPublicSite={true}
      practitionerSlug={slug}
      policies={policies}
      canonicalProfilePhoto={practitionerProfile?.photo || null}
    />
    </>
  );
};

export default PublicWebsite;
